import {
  normalizeStoredSessionIntent,
  type AgentRuntimeInteractionRequest,
  type AgentType,
  type StoredSessionIntent,
} from "@cord/shared"
import { normalizeChatGptOauthDebug } from "@/lib/chatgpt-oauth-debug"
import {
  buildPendingInteractionsFromStatus,
  buildRuntimeStatusFromStatusPayload,
} from "@/lib/control-plane-runtime-snapshot"
import type { ThreadControlPlaneRuntimeSnapshotLike } from "@/lib/thread-sync-runtime-materialization"
import type {
  ActiveCredentialSourceSnapshot,
  ActiveEditMessageOperation,
  ChatGptOauthDebug,
  CodeServerStatus,
  CredentialIssueSnapshot,
  DevServerStatus,
  PreviewEndpoint,
  RemoteWsDiagnostics,
  RuntimeProcessSnapshot,
  SandboxStartupQueueKind,
  SandboxProgress,
  SandboxStatus,
  SessionAgentIdentity,
  YoloFlowStep,
} from "@/hooks/thread-sync-client-types"

export type ThreadControlPlaneState = {
  initReady: boolean
  agentType: SessionAgentIdentity
  sessionStatus: string
  connectionCount: number
  statusObservedAt: number | null
  branchSwitchInProgress: boolean
  startNewWorkGitPending: boolean
  firstDispatchBranchRenameInProgress: boolean
  sandboxStatus: SandboxStatus
  sandboxProgress: SandboxProgress[]
  sandboxStartupKind: SandboxStartupQueueKind | null
  /** How this sandbox was booted: "bare" | "prebuilt" | "snapshot" | "resume" | null */
  bootSource: string | null
  ptyUrl: string | null
  vscodeUrl: string | null
  previewUrl: string | null
  previewEndpoints: PreviewEndpoint[]
  runtimeProcesses: RuntimeProcessSnapshot[]
  devServerStatus: DevServerStatus
  devServerError: string | null
  runtimeProcessesConfigured: boolean
  activeCredentialSource: ActiveCredentialSourceSnapshot | null
  nextCredentialSource: ActiveCredentialSourceSnapshot | null
  credentialIssue: CredentialIssueSnapshot | null
  credentialStateRefreshedAt: number | null
  lastCredentialFallbackAt: number | null
  runtimeLastErrorCode: string | null
  runtimeLastErrorMessage: string | null
  conversationEpoch: number
  activeEditMessageOperation: ActiveEditMessageOperation | null
  chatGptOauthDebug: ChatGptOauthDebug | null
  yoloFlowStep: YoloFlowStep
  yoloFlowError: string | null
  yoloFlowUpdatedAt: number | null
  remoteWsDiagnostics: RemoteWsDiagnostics | null
  runtimeSnapshot: ThreadControlPlaneRuntimeSnapshotLike | null
  prAdoption: PrAdoptionControlPlaneSnapshot | null
  prAdoptionFreshSession: boolean | null
  sessionModelIntent: StoredSessionIntent | null | undefined
  sessionModelIntentConversationId?: string | null
}

export type PrAdoptionControlPlaneSnapshot = {
  operationId: string
  status: string
  step: string
  prNumber: number
  title: string
  url: string
  branchName: string
  baseBranch: string
  errorCode?: string | null
  errorMessage?: string | null
}

export type NormalizedResolveConflictState = {
  requestId: string
  conversationId: string | null
  state: "started" | "dispatched" | "failed"
  baseBranch: string | null
  dispatchMode: "queued" | "forwarded" | null
  error: string | null
  timestamp: number
}

export type NormalizedThreadStatusPayload = {
  row: Record<string, unknown>
  agentType: SessionAgentIdentity
  knownAgentType: AgentType | null
  controlPlaneSnapshotAccepted: boolean
  controlPlane: ThreadControlPlaneState
  pendingMessagesRaw: unknown[]
  pendingSessionActionsRaw: unknown[]
  threadStatus: {
    activityState: string | null
    lastActivityEventId: number | null
    lastFinishedEventId: number | null
    lastFinishedAt: number | null
    lastFinishReason: string | null
    sandboxStatus: string | null
    persistedStatus: string | null
    lifecycleStatus: string | null
    statusObservedAt: number | null
  }
  resolveConflict: NormalizedResolveConflictState | null
  resolveConflictObservedAt: number | null
  resolveConflictObservedConversationId: string | null
  yoloFlow: {
    step: unknown
    error: unknown
    timestamp: unknown
    mergeFailureKind: unknown
  }
  prFlowSnapshot: unknown
  sessionModelIntent: StoredSessionIntent | null | undefined
  sessionModelIntentConversationId: string | null
}

const EMPTY_PROGRESS: SandboxProgress[] = []
const EMPTY_ENDPOINTS: PreviewEndpoint[] = []
const EMPTY_PROCESSES: RuntimeProcessSnapshot[] = []
// Older status payloads did not annotate model-intent scope; they represented
// the default conversation/session-level intent.
const DEFAULT_SESSION_MODEL_INTENT_CONVERSATION_ID = "default"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  return Math.floor(value)
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null
}

function normalizeCredentialSourceSnapshot(value: unknown): ActiveCredentialSourceSnapshot | null {
  const row = asRecord(value)
  const ref = asString(row?.ref)
  const providerID = asString(row?.providerID)
  const kind = row?.kind === "api_key" || row?.kind === "subscription"
    ? row.kind
    : null
  const label = asString(row?.label)
  if (!ref || !providerID || !kind || !label) {
    return null
  }

  return {
    ref,
    providerID,
    kind,
    label,
    note: typeof row?.note === "string" ? row.note : null,
    oauthMode:
      row?.oauthMode === "chatgpt_codex" || row?.oauthMode === "claude_setup_token"
        ? row.oauthMode
        : null,
    keyRowId: asString(row?.keyRowId),
  }
}

function normalizeCredentialIssueSnapshot(value: unknown): CredentialIssueSnapshot | null {
  const row = asRecord(value)
  const code = asString(row?.code)
  const message = asString(row?.message)
  const detectedAt = asNumber(row?.detectedAt)
  const classification =
    row?.classification === "authentication_failed"
    || row?.classification === "billing_error"
    || row?.classification === "rate_limit"
    || row?.classification === "insufficient_quota"
    || row?.classification === "usage_not_included"
      ? row.classification
      : null
  if (!code || !message || detectedAt === null || classification === null) {
    return null
  }

  return {
    code,
    message,
    classification,
    detectedAt,
  }
}

// Full `status` payloads can arrive out of order when a slower HTTP probe or a
// delayed websocket frame races a fresher snapshot. We only let a status
// snapshot rewrite preview/runtime control-plane fields when its server-authored
// freshness token is current. This specifically protects sandbox-restart flows
// where an older `running` snapshot could otherwise hide that the replacement
// runtime is still idle and leave the preview loader stuck probing forever.
function shouldAcceptStatusSnapshot(args: {
  previousObservedAt: number | null
  incomingObservedAt: number | null
}): boolean {
  if (args.incomingObservedAt !== null) {
    return args.previousObservedAt === null || args.incomingObservedAt >= args.previousObservedAt
  }

  return args.previousObservedAt === null
}

export function normalizeSessionAgentIdentity(value: unknown): SessionAgentIdentity {
  if (value === "opencode" || value === "claude_code" || value === "cursor") {
    return value
  }
  return "unknown"
}

export function toKnownAgentType(agentType: SessionAgentIdentity): AgentType | null {
  if (agentType === "unknown") return null
  return agentType
}

export function normalizeSandboxStatus(value: unknown): SandboxStatus {
  return value === "creating" ||
    value === "starting_services" ||
    value === "starting_agent" ||
    value === "starting_opencode" ||
    value === "connecting_sse" ||
    value === "running" ||
    value === "stopped" ||
    value === "error"
    ? value
    : "none"
}

function isSandboxStartupStatus(status: SandboxStatus): boolean {
  return (
    status === "creating" ||
    status === "starting_services" ||
    status === "starting_agent" ||
    status === "starting_opencode" ||
    status === "connecting_sse"
  )
}

function normalizeSandboxStartupKind(value: unknown): SandboxStartupQueueKind | null {
  return value === "start" || value === "resume" || value === "recover" || value === "restore" ? value : null
}

export function normalizeYoloFlowStep(value: unknown): YoloFlowStep {
  return value === "quick_pr" ||
    value === "merge_pr" ||
    value === "archiving" ||
    value === "done" ||
    value === "error"
    ? value
    : "idle"
}

export function normalizeDevServerStatus(value: unknown): DevServerStatus {
  return value === "starting" || value === "running" || value === "error" ? value : "idle"
}

export function normalizePreviewEndpoint(value: unknown): PreviewEndpoint | null {
  const row = asRecord(value)
  const endpointId = asString(row?.endpointId)
  const label = asString(row?.label)
  const url = asString(row?.url)
  if (!endpointId || !label || !url) return null
  return {
    endpointId,
    processId: asString(row?.processId),
    processName: asString(row?.processName) ?? asString(row?.process) ?? null,
    endpointName: asString(row?.endpointName) ?? asString(row?.name) ?? label,
    label,
    url,
    port: asNumber(row?.port),
    exposure: asString(row?.exposure) ?? undefined,
  }
}

function normalizeRuntimeProcessEndpoint(value: unknown): PreviewEndpoint | null {
  const row = asRecord(value)
  const endpointId = asString(row?.endpointId) ?? asString(row?.id)
  const label = asString(row?.label) ?? asString(row?.name) ?? endpointId
  if (!endpointId || !label) return null
  return {
    endpointId,
    processId: asString(row?.processId),
    processName: asString(row?.processName) ?? asString(row?.process) ?? null,
    endpointName: asString(row?.endpointName) ?? asString(row?.name) ?? label,
    label,
    url: asString(row?.url) ?? "",
    port: asNumber(row?.port),
    exposure: asString(row?.exposure) ?? undefined,
  }
}

export function normalizeRuntimeProcess(value: unknown): RuntimeProcessSnapshot | null {
  const row = asRecord(value)
  const id = asString(row?.id)
  // The server sends `id` and `name` for processes. Fall back to `id` for `key`
  // and `name` for `label` so runtime processes aren't silently dropped when
  // the status payload uses the canonical server-side shape.
  const key = asString(row?.key) ?? id
  const name = asString(row?.name)
  const label = asString(row?.label) ?? name ?? id
  if (!id || !key || !label) return null
  return {
    id,
    key,
    name: name ?? label,
    label,
    status: normalizeDevServerStatus(row?.status),
    command: asString(row?.command),
    url: asString(row?.url),
    endpoints: Array.isArray(row?.endpoints)
      ? row.endpoints
          .map((endpoint) => normalizeRuntimeProcessEndpoint(endpoint))
          .filter((endpoint): endpoint is PreviewEndpoint => endpoint !== null)
      : [],
  }
}

export function normalizePrAdoptionSnapshot(value: unknown): PrAdoptionControlPlaneSnapshot | null {
  const row = asRecord(value)
  if (!row) return null
  const operationId = asString(row.operationId)
  const status = asString(row.status)
  const step = asString(row.step)
  const prNumber = asNumber(row.prNumber)
  const title = asString(row.title)
  const url = asString(row.url)
  const branchName = asString(row.branchName)
  const baseBranch = asString(row.baseBranch)
  if (!operationId || !status || !step || !prNumber || !title || !url || !branchName || !baseBranch) {
    return null
  }
  return {
    operationId,
    status,
    step,
    prNumber,
    title,
    url,
    branchName,
    baseBranch,
    errorCode: asString(row.errorCode) ?? null,
    errorMessage: asString(row.errorMessage) ?? null,
  }
}

function normalizeActiveEditOperationState(
  value: unknown,
): ActiveEditMessageOperation["state"] | null {
  return asString(value)
}

export function normalizeActiveEditOperation(value: unknown): ActiveEditMessageOperation | null {
  const row = asRecord(value)
  const state = normalizeActiveEditOperationState(row?.state)
  if (!state) return null
  return {
    state,
    requestId: asString(row?.requestId),
    messageId: asString(row?.messageId),
    operationId: asString(row?.operationId) ?? undefined,
    conversationId: asString(row?.conversationId) ?? undefined,
    timestamp: asNumber(row?.timestamp) ?? undefined,
    reverted: asBoolean(row?.reverted) ?? undefined,
    error: asRecord(row?.error) ?? undefined,
  }
}

export function emptyControlPlaneState(initialBranchSwitchInProgress = false): ThreadControlPlaneState {
  return {
    initReady: false,
    agentType: "unknown",
    sessionStatus: "disconnected",
    connectionCount: 0,
    statusObservedAt: null,
    branchSwitchInProgress: initialBranchSwitchInProgress,
    startNewWorkGitPending: false,
    firstDispatchBranchRenameInProgress: false,
    sandboxStatus: "none",
    sandboxProgress: EMPTY_PROGRESS,
    sandboxStartupKind: null,
    bootSource: null,
    ptyUrl: null,
    vscodeUrl: null,
    previewUrl: null,
    previewEndpoints: EMPTY_ENDPOINTS,
    runtimeProcesses: EMPTY_PROCESSES,
    devServerStatus: "idle",
    devServerError: null,
    runtimeProcessesConfigured: false,
    activeCredentialSource: null,
    nextCredentialSource: null,
    credentialIssue: null,
    credentialStateRefreshedAt: null,
    lastCredentialFallbackAt: null,
    runtimeLastErrorCode: null,
    runtimeLastErrorMessage: null,
    conversationEpoch: 0,
    activeEditMessageOperation: null,
    chatGptOauthDebug: null,
    yoloFlowStep: "idle",
    yoloFlowError: null,
    yoloFlowUpdatedAt: null,
    remoteWsDiagnostics: null,
    runtimeSnapshot: null,
    prAdoption: null,
    prAdoptionFreshSession: null,
    sessionModelIntent: undefined,
    sessionModelIntentConversationId: null,
  }
}

export function deriveCodeServerStateFromStatusPayload(args: {
  currentCodeServerStatus: CodeServerStatus
  controlPlaneVscodeUrl: string | null
}): {
  codeServerStatus: CodeServerStatus
  shouldClearError: boolean
} {
  const codeServerStatus = args.currentCodeServerStatus === "starting" || args.currentCodeServerStatus === "error"
    ? args.currentCodeServerStatus
    : args.currentCodeServerStatus === "running" && args.controlPlaneVscodeUrl
      ? "running"
      : "idle"

  return {
    codeServerStatus,
    shouldClearError: codeServerStatus !== "error",
  }
}

export function normalizeThreadStatusPayload(args: {
  payload: unknown
  previousControlPlane: ThreadControlPlaneState
  existingInteractions?: AgentRuntimeInteractionRequest[]
  nowMs: number
}): NormalizedThreadStatusPayload {
  const row = asRecord(args.payload) ?? {}
  const hasInitReady = Object.prototype.hasOwnProperty.call(row, "initReady")
  const hasConnections = Object.prototype.hasOwnProperty.call(row, "connections")
  const hasSandboxStatus = Object.prototype.hasOwnProperty.call(row, "sandboxStatus")
  const hasSandboxStartupKind = Object.prototype.hasOwnProperty.call(row, "sandboxStartupKind")
  const hasBootSource = Object.prototype.hasOwnProperty.call(row, "bootSource")
  const hasPtyUrl = Object.prototype.hasOwnProperty.call(row, "ptyUrl")
  const hasVscodeUrl = Object.prototype.hasOwnProperty.call(row, "vscodeUrl")
  const hasPreviewUrl = Object.prototype.hasOwnProperty.call(row, "previewUrl")
  const hasPreviewEndpoints = Object.prototype.hasOwnProperty.call(row, "previewEndpoints")
  const hasRuntimeProcesses = Object.prototype.hasOwnProperty.call(row, "runtimeProcesses")
  const hasDevServerStatus = Object.prototype.hasOwnProperty.call(row, "devServerStatus")
  const hasDevServerError = Object.prototype.hasOwnProperty.call(row, "devServerError")
  const hasRuntimeProcessesConfigured = Object.prototype.hasOwnProperty.call(row, "runtimeProcessesConfigured")
  const hasActiveCredentialSource = Object.prototype.hasOwnProperty.call(row, "activeCredentialSource")
  const hasNextCredentialSource = Object.prototype.hasOwnProperty.call(row, "nextCredentialSource")
  const hasCredentialIssue = Object.prototype.hasOwnProperty.call(row, "credentialIssue")
  const hasCredentialStateRefreshedAt = Object.prototype.hasOwnProperty.call(row, "credentialStateRefreshedAt")
  const hasLastCredentialFallbackAt = Object.prototype.hasOwnProperty.call(row, "lastCredentialFallbackAt")
  const hasRuntimeLastErrorCode = Object.prototype.hasOwnProperty.call(row, "runtimeLastErrorCode")
  const hasRuntimeLastErrorMessage = Object.prototype.hasOwnProperty.call(row, "runtimeLastErrorMessage")
  const hasConversationEpoch = Object.prototype.hasOwnProperty.call(row, "conversationEpoch")
  const hasActiveEditOperation = Object.prototype.hasOwnProperty.call(row, "activeEditOperation")
  const hasChatGptOauthDebug = Object.prototype.hasOwnProperty.call(row, "chatgptOauthDebug")
  const hasWsDiagnostics = Object.prototype.hasOwnProperty.call(row, "wsDiagnostics")
  const hasPrAdoption = Object.prototype.hasOwnProperty.call(row, "prAdoption")
  const hasPrAdoptionFreshSession = Object.prototype.hasOwnProperty.call(row, "prAdoptionFreshSession")
  const hasRuntimeSnapshotSignal =
    Object.prototype.hasOwnProperty.call(row, "pendingQuestions")
    || Object.prototype.hasOwnProperty.call(row, "pendingPermissions")
    || Object.prototype.hasOwnProperty.call(row, "agentStatus")
    || Object.prototype.hasOwnProperty.call(row, "activityState")
    || Object.prototype.hasOwnProperty.call(row, "activityTransitionAt")
    || Object.prototype.hasOwnProperty.call(row, "lastFinishedAt")
    || Object.prototype.hasOwnProperty.call(row, "lastFinishReason")
    || hasRuntimeLastErrorCode
    || hasRuntimeLastErrorMessage
    || Object.prototype.hasOwnProperty.call(row, "replicationIntegrity")
    || Object.prototype.hasOwnProperty.call(row, "runtimeFinality")
    || hasConversationEpoch
  const incomingStatusObservedAt = asNumber(row.statusObservedAt)
  const controlPlaneSnapshotAccepted = shouldAcceptStatusSnapshot({
    previousObservedAt: args.previousControlPlane.statusObservedAt,
    incomingObservedAt: incomingStatusObservedAt,
  })
  const agentType = normalizeSessionAgentIdentity(row.agentType)
  const knownAgentType = toKnownAgentType(agentType)
  const pendingQuestions = Array.isArray(row.pendingQuestions)
    ? row.pendingQuestions
        .map((value) => asRecord(value))
        .filter((value): value is Record<string, unknown> => value !== null)
    : []
  const pendingPermissions = Array.isArray(row.pendingPermissions)
    ? row.pendingPermissions
        .map((value) => asRecord(value))
        .filter((value): value is Record<string, unknown> => value !== null)
    : []

  const runtimeSnapshot = knownAgentType
    ? (() => {
        const pendingInteractions = buildPendingInteractionsFromStatus({
          agentType: knownAgentType,
          pendingQuestions,
          pendingPermissions,
          existingInteractions: args.existingInteractions,
        })
        const runtimeStatus = buildRuntimeStatusFromStatusPayload({
          agentType: knownAgentType,
          pendingInteractionCount: pendingInteractions.length,
          agentStatus: asRecord(row.agentStatus),
          activityState: asString(row.activityState),
          activityTransitionAt: asNumber(row.activityTransitionAt),
          lastFinishedAt: asNumber(row.lastFinishedAt),
          lastFinishReason: asString(row.lastFinishReason),
          runtimeLastErrorCode: asString(row.runtimeLastErrorCode),
          runtimeLastErrorMessage: asString(row.runtimeLastErrorMessage),
          replicationIntegrity: asRecord(row.replicationIntegrity),
          runtimeFinality: asRecord(row.runtimeFinality),
        })
        return {
          agentType: knownAgentType,
          pendingInteractions,
          status: runtimeStatus,
          conversationEpoch: asNumber(row.conversationEpoch) ?? 0,
        }
      })()
    : null

  const resolveConflictState = asRecord(row.resolveConflictState)
  const resolveConflictRequestId = asString(resolveConflictState?.requestId)
  const resolveConflictPhase = asString(resolveConflictState?.state)
  const normalizedResolveConflictPhase: NormalizedResolveConflictState["state"] | null =
    resolveConflictPhase === "started"
      ? "started"
      : resolveConflictPhase === "dispatched"
        ? "dispatched"
        : resolveConflictPhase === "failed"
          ? "failed"
          : null
  const resolveConflict =
    resolveConflictRequestId &&
    normalizedResolveConflictPhase
      ? {
          requestId: resolveConflictRequestId,
          conversationId: asString(resolveConflictState?.conversationId),
          state: normalizedResolveConflictPhase,
          baseBranch: asString(resolveConflictState?.baseBranch),
          dispatchMode:
            asString(resolveConflictState?.dispatchMode) === "queued" ||
            asString(resolveConflictState?.dispatchMode) === "forwarded"
              ? (asString(resolveConflictState?.dispatchMode) as "queued" | "forwarded")
              : null,
          error:
            asString(resolveConflictState?.error) ??
            (normalizedResolveConflictPhase === "failed" ? "Failed to start conflict resolution" : null),
          timestamp: asNumber(resolveConflictState?.timestamp) ?? args.nowMs,
        }
      : null

  const hasSessionModelIntent = Object.prototype.hasOwnProperty.call(row, "sessionModelIntent")
  const sessionModelIntent = hasSessionModelIntent
    ? row.sessionModelIntent === null
      ? null
      : normalizeStoredSessionIntent(row.sessionModelIntent)
    : args.previousControlPlane.sessionModelIntent
  const sessionModelIntentConversationId = Object.prototype.hasOwnProperty.call(row, "sessionModelIntentConversationId")
    ? asString(row.sessionModelIntentConversationId)
    : hasSessionModelIntent
      ? DEFAULT_SESSION_MODEL_INTENT_CONVERSATION_ID
      : args.previousControlPlane.sessionModelIntentConversationId
  const nextSandboxStatus = controlPlaneSnapshotAccepted
    ? hasSandboxStatus
      ? normalizeSandboxStatus(row.sandboxStatus)
      : args.previousControlPlane.sandboxStatus
    : args.previousControlPlane.sandboxStatus
  const nextBootSource = controlPlaneSnapshotAccepted
    ? hasBootSource
      ? asString(row.bootSource)
      : isSandboxStartupStatus(nextSandboxStatus)
        ? null
        : args.previousControlPlane.bootSource
    : args.previousControlPlane.bootSource
  const nextSandboxStartupKind = controlPlaneSnapshotAccepted
    ? hasSandboxStartupKind
      ? normalizeSandboxStartupKind(row.sandboxStartupKind)
      : isSandboxStartupStatus(nextSandboxStatus)
        ? args.previousControlPlane.sandboxStartupKind
        : null
    : args.previousControlPlane.sandboxStartupKind

  return {
    row,
    agentType,
    knownAgentType,
    controlPlaneSnapshotAccepted,
    controlPlane: {
      initReady: controlPlaneSnapshotAccepted
        ? hasInitReady
          ? row.initReady === true
          : args.previousControlPlane.initReady
        : args.previousControlPlane.initReady,
      agentType: controlPlaneSnapshotAccepted
        ? agentType
        : args.previousControlPlane.agentType,
      sessionStatus: controlPlaneSnapshotAccepted
        ? asString(row.status) ?? args.previousControlPlane.sessionStatus
        : args.previousControlPlane.sessionStatus,
      connectionCount: controlPlaneSnapshotAccepted
        ? hasConnections
          ? asNumber(row.connections) ?? args.previousControlPlane.connectionCount
          : args.previousControlPlane.connectionCount
        : args.previousControlPlane.connectionCount,
      statusObservedAt: controlPlaneSnapshotAccepted
        ? incomingStatusObservedAt ?? args.previousControlPlane.statusObservedAt
        : args.previousControlPlane.statusObservedAt,
      branchSwitchInProgress: controlPlaneSnapshotAccepted
        ? asBoolean(row.branchSwitchInProgress) ?? args.previousControlPlane.branchSwitchInProgress
        : args.previousControlPlane.branchSwitchInProgress,
      startNewWorkGitPending: controlPlaneSnapshotAccepted
        ? asBoolean(row.startNewWorkGitPending) ?? args.previousControlPlane.startNewWorkGitPending
        : args.previousControlPlane.startNewWorkGitPending,
      firstDispatchBranchRenameInProgress: controlPlaneSnapshotAccepted
        ? asBoolean(row.firstDispatchBranchRenameInProgress)
          ?? args.previousControlPlane.firstDispatchBranchRenameInProgress
        : args.previousControlPlane.firstDispatchBranchRenameInProgress,
      sandboxStatus: nextSandboxStatus,
      sandboxProgress: args.previousControlPlane.sandboxProgress,
      sandboxStartupKind: nextSandboxStartupKind,
      bootSource: nextBootSource,
      ptyUrl: controlPlaneSnapshotAccepted
        ? hasPtyUrl
          ? asString(row.ptyUrl)
          : args.previousControlPlane.ptyUrl
        : args.previousControlPlane.ptyUrl,
      vscodeUrl: controlPlaneSnapshotAccepted
        ? hasVscodeUrl
          ? asString(row.vscodeUrl)
          : args.previousControlPlane.vscodeUrl
        : args.previousControlPlane.vscodeUrl,
      previewUrl: controlPlaneSnapshotAccepted
        ? hasPreviewUrl
          ? asString(row.previewUrl)
          : args.previousControlPlane.previewUrl
        : args.previousControlPlane.previewUrl,
      previewEndpoints: controlPlaneSnapshotAccepted
        ? hasPreviewEndpoints
          ? Array.isArray(row.previewEndpoints)
            ? row.previewEndpoints
                .map((value) => normalizePreviewEndpoint(value))
                .filter((value): value is PreviewEndpoint => value !== null)
            : EMPTY_ENDPOINTS
          : args.previousControlPlane.previewEndpoints
        : args.previousControlPlane.previewEndpoints,
      runtimeProcesses: controlPlaneSnapshotAccepted
        ? hasRuntimeProcesses
          ? Array.isArray(row.runtimeProcesses)
            ? row.runtimeProcesses
                .map((value) => normalizeRuntimeProcess(value))
                .filter((value): value is RuntimeProcessSnapshot => value !== null)
            : EMPTY_PROCESSES
          : args.previousControlPlane.runtimeProcesses
        : args.previousControlPlane.runtimeProcesses,
      devServerStatus: controlPlaneSnapshotAccepted
        ? hasDevServerStatus
          ? normalizeDevServerStatus(row.devServerStatus)
          : args.previousControlPlane.devServerStatus
        : args.previousControlPlane.devServerStatus,
      devServerError: controlPlaneSnapshotAccepted
        ? hasDevServerError
          ? asString(row.devServerError)
          : args.previousControlPlane.devServerError
        : args.previousControlPlane.devServerError,
      runtimeProcessesConfigured: controlPlaneSnapshotAccepted
        ? hasRuntimeProcessesConfigured
          ? row.runtimeProcessesConfigured === true
          : args.previousControlPlane.runtimeProcessesConfigured
        : args.previousControlPlane.runtimeProcessesConfigured,
      activeCredentialSource: controlPlaneSnapshotAccepted
        ? hasActiveCredentialSource
          ? normalizeCredentialSourceSnapshot(row.activeCredentialSource)
          : args.previousControlPlane.activeCredentialSource
        : args.previousControlPlane.activeCredentialSource,
      nextCredentialSource: controlPlaneSnapshotAccepted
        ? hasNextCredentialSource
          ? normalizeCredentialSourceSnapshot(row.nextCredentialSource)
          : args.previousControlPlane.nextCredentialSource
        : args.previousControlPlane.nextCredentialSource,
      credentialIssue: controlPlaneSnapshotAccepted
        ? hasCredentialIssue
          ? normalizeCredentialIssueSnapshot(row.credentialIssue)
          : args.previousControlPlane.credentialIssue
        : args.previousControlPlane.credentialIssue,
      credentialStateRefreshedAt: controlPlaneSnapshotAccepted
        ? hasCredentialStateRefreshedAt
          ? asNumber(row.credentialStateRefreshedAt)
          : args.previousControlPlane.credentialStateRefreshedAt
        : args.previousControlPlane.credentialStateRefreshedAt,
      lastCredentialFallbackAt: controlPlaneSnapshotAccepted
        ? hasLastCredentialFallbackAt
          ? asNumber(row.lastCredentialFallbackAt)
          : args.previousControlPlane.lastCredentialFallbackAt
        : args.previousControlPlane.lastCredentialFallbackAt,
      runtimeLastErrorCode: controlPlaneSnapshotAccepted
        ? hasRuntimeLastErrorCode
          ? asString(row.runtimeLastErrorCode)
          : args.previousControlPlane.runtimeLastErrorCode
        : args.previousControlPlane.runtimeLastErrorCode,
      runtimeLastErrorMessage: controlPlaneSnapshotAccepted
        ? hasRuntimeLastErrorMessage
          ? asString(row.runtimeLastErrorMessage)
          : args.previousControlPlane.runtimeLastErrorMessage
        : args.previousControlPlane.runtimeLastErrorMessage,
      conversationEpoch: controlPlaneSnapshotAccepted
        ? hasConversationEpoch
          ? asNumber(row.conversationEpoch) ?? 0
          : args.previousControlPlane.conversationEpoch
        : args.previousControlPlane.conversationEpoch,
      activeEditMessageOperation: controlPlaneSnapshotAccepted
        ? hasActiveEditOperation
          ? normalizeActiveEditOperation(row.activeEditOperation)
          : args.previousControlPlane.activeEditMessageOperation
        : args.previousControlPlane.activeEditMessageOperation,
      chatGptOauthDebug: controlPlaneSnapshotAccepted
        ? hasChatGptOauthDebug
          ? normalizeChatGptOauthDebug(row.chatgptOauthDebug)
          : args.previousControlPlane.chatGptOauthDebug
        : args.previousControlPlane.chatGptOauthDebug,
      yoloFlowStep: args.previousControlPlane.yoloFlowStep,
      yoloFlowError: args.previousControlPlane.yoloFlowError,
      yoloFlowUpdatedAt: args.previousControlPlane.yoloFlowUpdatedAt,
      remoteWsDiagnostics: controlPlaneSnapshotAccepted
        ? hasWsDiagnostics
          ? (asRecord(row.wsDiagnostics) as RemoteWsDiagnostics | null) ?? null
          : args.previousControlPlane.remoteWsDiagnostics
        : args.previousControlPlane.remoteWsDiagnostics,
      runtimeSnapshot: controlPlaneSnapshotAccepted
        ? hasRuntimeSnapshotSignal
          ? runtimeSnapshot
          : args.previousControlPlane.runtimeSnapshot
        : args.previousControlPlane.runtimeSnapshot,
      prAdoption: controlPlaneSnapshotAccepted
        ? hasPrAdoption
          ? normalizePrAdoptionSnapshot(row.prAdoption)
          : args.previousControlPlane.prAdoption
        : args.previousControlPlane.prAdoption,
      prAdoptionFreshSession: controlPlaneSnapshotAccepted
        ? hasPrAdoptionFreshSession
          ? asBoolean(row.prAdoptionFreshSession)
          : args.previousControlPlane.prAdoptionFreshSession
        : args.previousControlPlane.prAdoptionFreshSession,
      sessionModelIntent: controlPlaneSnapshotAccepted
        ? sessionModelIntent
        : args.previousControlPlane.sessionModelIntent,
      sessionModelIntentConversationId: controlPlaneSnapshotAccepted
        ? sessionModelIntentConversationId
        : args.previousControlPlane.sessionModelIntentConversationId,
    },
    pendingMessagesRaw: Array.isArray(row.pendingMessages) ? row.pendingMessages : [],
    pendingSessionActionsRaw: Array.isArray(row.pendingSessionActions) ? row.pendingSessionActions : [],
    threadStatus: {
      activityState: asString(row.activityState),
      lastActivityEventId: asNumber(row.lastActivityEventId),
      lastFinishedEventId: asNumber(row.lastFinishedEventId),
      lastFinishedAt: asNumber(row.lastFinishedAt),
      lastFinishReason: asString(row.lastFinishReason),
      sandboxStatus: hasSandboxStatus
        ? asString(row.sandboxStatus)
        : args.previousControlPlane.sandboxStatus,
      persistedStatus: asString(row.status),
      lifecycleStatus: asString(row.lifecycleStatus),
      statusObservedAt: incomingStatusObservedAt,
    },
    resolveConflict,
    resolveConflictObservedAt: asNumber(row.resolveConflictStateObservedAt),
    resolveConflictObservedConversationId: asString(row.resolveConflictStateConversationId),
    yoloFlow: {
      step: asRecord(row.yoloFlow)?.step,
      error: asRecord(row.yoloFlow)?.error,
      timestamp: asRecord(row.yoloFlow)?.updatedAt,
      mergeFailureKind: asRecord(row.yoloFlow)?.mergeFailureKind,
    },
    prFlowSnapshot: row.prFlow,
    sessionModelIntent,
    sessionModelIntentConversationId: sessionModelIntentConversationId ?? null,
  }
}
